<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";

import { toast } from "@/common/utils/toast";

import { tagList } from "@/common/api/rest/tag";
import {
  consoleTagList,
  consoleTagCreate,
  consoleTagDel,
} from "@/common/api/rest/console/tag";

import type { TagType } from "@/common/types/tag";
import type { CollectionTagType } from "@/common/types/collection-tag";

import { sidebarListCheckbox, svgIcon } from "@/common/components";

const { t } = useI18n();

const props = defineProps<{
  consoleId: string;
}>();

const tags = ref<TagType[]>([]);
const consoleTags = ref<CollectionTagType[]>([]);

const loading = ref(false);
const showSidebar = ref(false);

const added = computed(() => consoleTags.value.map((item) => item.tag_id));

async function fetchList() {
  await tagList({
    full: true,
    sort: "ASC",
    sort_by: "name",
  })
    .then((response) => {
      const { success, data, message } = response;

      if (success) {
        tags.value = data.list;
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    })
    .finally(() => {
      loading.value = false;
    });
}

async function fetchItems() {
  await consoleTagList(props.consoleId)
    .then((response) => {
      const { success, data, message } = response;

      if (success) {
        consoleTags.value = data.list;
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

async function addItem(item: TagType) {
  await consoleTagCreate(props.consoleId, item.tag_id)
    .then((response) => {
      const { success, message } = response;

      if (success) {
        fetchItems();
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

async function removeItem(data: TagType) {
  const item = consoleTags.value.find((item) => item.tag_id === data.tag_id);

  if (!item) return;

  await consoleTagDel(item.item_id)
    .then((response) => {
      const { success, message } = response;

      if (success) {
        fetchItems();
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

async function openSidebar() {
  await fetchList();
  showSidebar.value = true;
}

function closeSidebar() {
  showSidebar.value = false;
}

onMounted(() => {
  fetchItems();
});
</script>

<template>
  <section class="tab-details">
    <div class="list">
      <div class="header">
        <div class="title">
          {{ t("collection.labels.tags") }}
        </div>
        <button class="btn btn-default w-8 h-8 p-0" @click="openSidebar()">
          <svg-icon icon="bars" size="1rem" />
        </button>
      </div>
      <div class="items">
        <template v-for="(item, itemIndex) in consoleTags" :key="itemIndex">
          <div class="item">
            <div class="item-header">
              <div class="name">
                {{ item.name }}
              </div>
              <button
                class="btn btn-default w-5 h-5 p-0"
                @click="removeItem(item as any)"
              >
                <svg-icon icon="close" size="0.75rem" />
              </button>
            </div>
          </div>
        </template>

        <template v-if="!consoleTags.length">
          <div class="empty-data">
            {{ t("collection.texts.empty_items") }}
          </div>
        </template>
      </div>
    </div>

    <template v-if="showSidebar">
      <sidebar-list-checkbox
        id="tag"
        :width="$store.getters.mobile ? '90vw' : '40rem'"
        :open="showSidebar"
        :loading="loading"
        :outside-close="true"
        :title="t('collection.labels.tags')"
        :list="tags"
        field-id="tag_id"
        field-option="name"
        :added="added"
        @close="closeSidebar()"
        @add:item="addItem"
        @remove:item="removeItem"
      />
    </template>
  </section>
</template>

<style lang="scss" scoped>
.tab-details {
  display: flex;
  flex-direction: column;

  .list {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    border-block-end: 1px solid var(--border);
    padding-block-end: 1rem;
    margin-block-end: 1rem;

    .header {
      display: flex;
      flex-direction: row;
      justify-content: space-between;
      align-items: center;
      margin-inline: 1rem;

      .title {
        font-size: 1.1rem;
        font-weight: 500;
      }
    }

    .items {
      display: flex;
      flex-wrap: wrap;
      padding-block: 1rem;
      padding-inline: 1rem;
      background: var(--white-off);
      border-radius: 5px;
      gap: 0.75rem;

      .item {
        display: flex;
        flex-direction: column;
        gap: 1rem;
        padding-block: 0.5rem;
        padding-inline: 0.75rem;
        background: white;
        border-radius: 5px;

        .item-header {
          display: flex;
          flex-direction: row;
          justify-content: space-between;
          align-items: center;
          gap: 0.5rem;
        }

        .item-content {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }
      }
    }
  }
}

@media screen and (max-width: 1024px) {
  .tab-details {
    .list {
      .header {
        .title {
          font-size: 1rem;
        }
      }

      .items {
        flex-direction: column;

        .item {
          width: 100%;

          .name {
            font-size: 0.875rem;
          }
        }
      }
    }
  }
}
</style>
